/**
 * BimEngine2d - 独立的 2D 图纸引擎
 * 轻量级独立类，不依赖 3D 引擎的 UI 管理器（toolbar、measure 等）
 * 用法：import { BimEngine2d } from 'iflow-engine'
 */
import { Engine2d } from './components/engine-2d';
import type { DrawingLoadOptions, Drawing2dLayer } from './components/engine-2d/types';
import { ManagerRegistry } from './core/manager-registry';
import { localeManager } from './services/locale';
import { themeManager } from './services/theme';
import type { LocaleType } from './locales/types';
import type { ThemeType } from './themes/types';
import type { EngineEvents } from './types/events';
import './iflow-engine-base.css';
/**
 * BimEngine2d 构造选项
 * 合并引擎配置与主题/语言设置
 */
export interface BimEngine2dOptions {
    /** 语言 */
    locale?: LocaleType;
    /** 主题 */
    theme?: ThemeType;
    /** 背景颜色（十六进制数值，如 0xffffff） */
    backgroundColor?: number;
    /** 是否启用网格 */
    gridEnabled?: boolean;
    /** 是否启用坐标轴 */
    axesEnabled?: boolean;
    /** 选中构件颜色 */
    selectionColor?: number;
    /** 高亮构件颜色 */
    highlightColor?: number;
    /** 是否启用性能监控 */
    enablePerformanceMonitoring?: boolean;
}

/**
 * 独立 2D 图纸引擎
 *
 * 内部复用 Engine2d 组件，跳过 Manager 层
 * 构造时自动初始化，可直接调用 loadDrawing() 加载图纸
 */
export class BimEngine2d {
    public container: HTMLElement;
    private registry: ManagerRegistry;
    private engineComponent: Engine2d | null = null;

    constructor(container: HTMLElement | string, options?: BimEngine2dOptions) {
        const el = typeof container === 'string' ? document.getElementById(container) : container;
        if (!el) throw new Error('Container not found');
        this.container = el;

        this.registry = new ManagerRegistry();

        if (options?.locale) localeManager.setLocale(options.locale);
        if (options?.theme && options.theme !== 'custom') {
            themeManager.setTheme(options.theme as 'dark' | 'light');
        }

        // 创建并初始化 2D 引擎
        this.engineComponent = new Engine2d({
            container: this.container,
            backgroundColor: options?.backgroundColor,
            gridEnabled: options?.gridEnabled,
            axesEnabled: options?.axesEnabled,
            selectionColor: options?.selectionColor,
            highlightColor: options?.highlightColor,
            enablePerformanceMonitoring: options?.enablePerformanceMonitoring,
        }, this.registry);

        this.engineComponent.init();
    }

    // ─── 图纸操作 ───

    /** 加载 2D 图纸 */
    public async loadDrawing(url: string, options?: DrawingLoadOptions): Promise<void> {
        return this.engineComponent?.loadDrawing(url, options);
    }

    /** 获取所有图层 */
    public getLayers(): Drawing2dLayer[] {
        return this.engineComponent?.getLayers() ?? [];
    }

    /** 设置图层可见性 */
    public setLayerVisible(name: string, visible: boolean): void {
        this.engineComponent?.setLayerVisible(name, visible);
    }

    // ─── 视图控制 ───

    /** 重置视图 */
    public resetView(): void {
        this.engineComponent?.resetView();
    }

    /** 适应视图（缩放到全部内容） */
    public fitToView(): void {
        this.engineComponent?.fitToView();
    }

    /** 设置缩放级别 */
    public setZoom(zoom: number): void {
        this.engineComponent?.setZoom(zoom);
    }

    /** 获取当前缩放级别 */
    public getZoom(): number {
        return this.engineComponent?.getZoom() ?? 1;
    }

    // ─── 主题 ───

    /** 设置主题 */
    public setTheme(theme: 'dark' | 'light'): void {
        themeManager.setTheme(theme);
    }

    // ─── 事件 ───

    /** 订阅事件 */
    public on<K extends keyof EngineEvents>(event: K, listener: (payload: EngineEvents[K]) => void): () => void {
        return this.registry.on(event, listener);
    }

    /** 取消订阅事件 */
    public off<K extends keyof EngineEvents>(event: K, listener: (payload: EngineEvents[K]) => void): void {
        this.registry.off(event, listener);
    }

    /** 订阅原始 2D 引擎事件 */
    public onRawEvent(event: string, handler: (...args: any[]) => void): void {
        this.engineComponent?.onRawEvent(event, handler);
    }

    /** 取消订阅原始 2D 引擎事件 */
    public offRawEvent(event: string, handler: (...args: any[]) => void): void {
        this.engineComponent?.offRawEvent(event, handler);
    }

    // ─── 生命周期 ───

    /** 销毁引擎，释放所有资源 */
    public destroy(): void {
        this.engineComponent?.destroy();
        this.engineComponent = null;
        this.registry.reset();
    }
}
